<template>
  <div class="flex flex-col gap-6">
    <h1 class="text_color text-xl font-semibold">Waterfall Enrichment</h1>
    <div
      class="overflow-y-scroll gap-[40px] flex flex-col"
      style="height: calc(100vh - (250px + var(--banner-height)))"
    >
      <SkeletonLoader v-if="loading" :page="loaderPages.WATERFALL" />

      <div
        v-else
        v-for="section in enrichmentSections"
        :key="section.id"
        class="flex flex-col gap-3"
      >
        <div class="flex flex-col gap-2 mb-3">
          <span class="proxima_nova text-lg text_color font-semibold">
            {{ section.title }}
          </span>
          <span class="proxima_nova text-[13px] secondary_color">
            {{ section.description }}
          </span>
        </div>

        <CustomContainer
          v-for="(card, index) in section.cards"
          :key="`${section.category}-${card.id}`"
          :class="['bg_4', { 'dragging-card': isDragging && draggedIndex === index && draggedCategory === section.category }]"
          :data-drag-category="section.category"
          padding="px-3 py-4"
        >
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center">
              <ThemeImage
                :icon="imageNames.DRAGGABLE_DOTS"
                alt="drag"
                class="drag-handle size-4 mr-3 cursor-grab active:cursor-grabbing"
                @mousedown.prevent="startDrag($event, index, section.category)"
              />
              <span class="color_666 proxima_nova text-base font-normal mr-4" style="line-height: 19px">
                {{ index === 0 ? "First try" : "Then try" }}
              </span>
              <img :src="card.icon" :alt="card.name" class="vendor_icon mr-4" />
              <span class="text_color proxima_nova text-base font-semibold" style="line-height: 19px">
                {{ card.name }}
              </span>
            </div>
            <div class="ml-4 w-1/3">
              <SearchableMultiSelectDropdown
                v-model="card.selectedOption"
                :multiple="false"
                :options="getDropdownOptions(card)"
                placeholder="Select option"
                width="100%"
                :match-trigger-width="true"
                search-placeholder="Search options"
                cta-text="Add Account"
                hide-header
                :disabled="card.isInternal"
                :show-clear="false"
                :allow-remove="isOptionRemovable"
                @selection-change="handleDropdownChange(index, $event, section.category)"
                @cta-click="handleAddAccount(card)"
                @remove-option="handleRemoveOption(card, $event)"
              />
            </div>
          </div>
        </CustomContainer>

        <EmptyScreen
          v-if="!section.cards?.length"
          :source="`waterfall_empty_${section.category}`"
          text=""
          :description="section.description"
          padding="my-8"
        />
      </div>
    </div>

    <div class="flex flex-row justify-end gap-2 footer py-6 px-[8vw]" style="margin-left: -8vw; margin-right: -8vw">
      <a-button class="secondary_medium" :disabled="!dirty" @click="onCancelChanges">Cancel</a-button>
      <a-button class="primary_medium" :disabled="!dirty || isSaving" :loading="isSaving" @click="onSaveChanges">Save changes</a-button>
    </div>

    <EnrichmentAddAccountModal
      :visible="addModalVisible"
      :provider-name="selectedVendorName"
      :submitting="isSubmittingAccount"
      :server-error="accountSubmitError"
      @update:visible="addModalVisible = $event"
      @account-added="handleAccountAdded"
    />

    <ConfirmationModal
      v-model:visible="removeModalVisible"
      :title="`Remove ${selectedVendorName} Account`"
      :message="`Are you sure you want to remove '${selectedAccountName}' ${selectedVendorName.toLowerCase()} account?`"
      type="danger"
      confirmText="Remove"
      :loading="isRemovingAccount"
      @confirm="handleAccountRemoved({ provider: selectedVendorName, accountName: selectedAccountName, optionData: selectedOptionData })"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { message } from "ant-design-vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SearchableMultiSelectDropdown from "@/src/components/SearchableMultiSelectDropdown.vue";
import EnrichmentAddAccountModal from "@/src/components/enrichment_components/EnrichmentAddAccountModal.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ProspectService from "@/src/api/ProspectService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { WATERFALL_ENRICHMENT_SECTIONS } from "@/src/constants/dataEnrichmentConstants.js";

// State
const loading = ref(true);
const dirty = ref(false);
const isSaving = ref(false);
const emailEnrichmentCards = ref([]);
const phoneEnrichmentCards = ref([]);
const originalEmailCards = ref([]);
const originalPhoneCards = ref([]);

// Drag state
const draggedIndex = ref(null);
const dragStartIndex = ref(null);
const draggedCategory = ref(null);
const isDragging = ref(false);

// Add account modal state
const addModalVisible = ref(false);
const isSubmittingAccount = ref(false);
const accountSubmitError = ref("");
const selectedVendorId = ref(null);
const selectedVendorName = ref("");

// Remove account modal state
const removeModalVisible = ref(false);
const isRemovingAccount = ref(false);
const selectedAccountName = ref("");
const selectedOptionData = ref({});

const enrichmentSections = computed(() =>
  WATERFALL_ENRICHMENT_SECTIONS.map((section) => ({
    ...section,
    cards: section.category === "email" ? emailEnrichmentCards.value : phoneEnrichmentCards.value,
  })),
);

const getCards = (category) =>
  category === "email" ? emailEnrichmentCards : phoneEnrichmentCards;

const getErrorMessage = (error) => {
  const data = error?.response?.data ?? error?.data ?? {};
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.message === "string") return data.message;
  if (typeof error?.message === "string") return error.message;
  return "Something went wrong. Please try again.";
};

const cloneCards = (cards) => JSON.parse(JSON.stringify(cards));

// Data fetching

const fetchWaterfallData = async () => {
  try {
    loading.value = true;
    const response = await ProspectService.fetchWaterfallVendors();
    const data = response || {};
    emailEnrichmentCards.value = mapVendorEntries(data.email || []);
    phoneEnrichmentCards.value = mapVendorEntries(data.phone || []);
    originalEmailCards.value = cloneCards(emailEnrichmentCards.value);
    originalPhoneCards.value = cloneCards(phoneEnrichmentCards.value);
    dirty.value = false;
  } catch (error) {
    message.error(getErrorMessage(error));
  } finally {
    loading.value = false;
  }
};

const mapVendorEntries = (entries) =>
  entries.map((entry) => ({
    id: entry.vendor.id,
    name: entry.vendor.name || "",
    icon: entry.vendor.icon || "",
    isInternal: entry.vendor.is_internal,
    isEnabled: entry.is_enabled,
    selectedOption: entry.active_account?.id || null,
    availableAccounts: entry.available_accounts || [],
  }));

onMounted(fetchWaterfallData);

// Dropdown

const getDropdownOptions = (card) =>
  (card.availableAccounts || []).map((acc) => ({
    label: acc.account_name,
    value: acc.id,
    accountData: acc,
  }));

const isOptionRemovable = (option) => option?.accountData?.type !== "managed";

const handleDropdownChange = (cardIndex, selectedValue, category) => {
  getCards(category).value[cardIndex].selectedOption = selectedValue;
  dirty.value = true;
};

// Add account

const handleAddAccount = (card) => {
  selectedVendorId.value = card.id;
  selectedVendorName.value = card.name;
  accountSubmitError.value = "";
  addModalVisible.value = true;
};

const handleAccountAdded = async (accountData) => {
  try {
    isSubmittingAccount.value = true;
    await ProspectService.createWaterfallAccount({
      vendor_id: selectedVendorId.value,
      account_name: accountData.accountName,
      api_key: accountData.apiKey,
    });
    await fetchWaterfallData();
    addModalVisible.value = false;
    message.success(`${selectedVendorName.value} account added successfully!`);
  } catch (error) {
    accountSubmitError.value = getErrorMessage(error);
  } finally {
    isSubmittingAccount.value = false;
  }
};

// Remove account

const handleRemoveOption = (card, optionInfo) => {
  const option = optionInfo?.option || optionInfo;
  if (option?.accountData?.type === "managed") return;
  selectedVendorName.value = card.name;
  selectedAccountName.value = optionInfo?.label || "";
  selectedOptionData.value = option || {};
  removeModalVisible.value = true;
};

const handleAccountRemoved = async (accountData) => {
  try {
    isRemovingAccount.value = true;
    const accountId = accountData?.optionData?.value;
    if (!accountId) return;
    await ProspectService.deleteWaterfallAccount(accountId);
    await fetchWaterfallData();
    removeModalVisible.value = false;
    message.success("Account removed successfully!");
  } catch (error) {
    message.error(getErrorMessage(error));
  } finally {
    isRemovingAccount.value = false;
  }
};

// Save / Cancel

const onSaveChanges = async () => {
  try {
    isSaving.value = true;
    await ProspectService.updateWaterfallConfig(buildConfigPayload());
    await fetchWaterfallData();
    dirty.value = false;
    message.success("Waterfall enrichment changes saved successfully!");
  } catch (error) {
    message.error(getErrorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const onCancelChanges = () => {
  emailEnrichmentCards.value = cloneCards(originalEmailCards.value);
  phoneEnrichmentCards.value = cloneCards(originalPhoneCards.value);
  dirty.value = false;
};

const buildConfigPayload = () => ({
  entries: ["email", "phone"].map((type) => {
    const cards = getCards(type).value;
    return {
      enrichment_type: type,
      vendor_order: cards.map((c) => c.id),
      disabled_vendors: cards.filter((c) => !c.isEnabled).map((c) => c.id),
      active_accounts: Object.fromEntries(
        cards.filter((c) => c.selectedOption && !c.isInternal).map((c) => [c.id, c.selectedOption]),
      ),
    };
  }),
});

// Drag and drop

const startDrag = (event, index, category) => {
  isDragging.value = true;
  draggedIndex.value = index;
  dragStartIndex.value = index;
  draggedCategory.value = category;
  document.body.style.cursor = "grabbing";
  document.body.style.userSelect = "none";
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
};

const onMouseMove = (event) => {
  if (draggedIndex.value === null || !draggedCategory.value) return;
  const category = draggedCategory.value;
  const cardEls = document.querySelectorAll(`[data-drag-category="${category}"]`);

  for (let i = 0; i < cardEls.length; i++) {
    if (i === draggedIndex.value) continue;
    const rect = cardEls[i].getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const shouldSwap =
      (i < draggedIndex.value && event.clientY < midY) ||
      (i > draggedIndex.value && event.clientY > midY);

    if (shouldSwap) {
      const cards = getCards(category);
      const newCards = [...cards.value];
      const [moved] = newCards.splice(draggedIndex.value, 1);
      newCards.splice(i, 0, moved);
      cards.value = newCards;
      draggedIndex.value = i;
      break;
    }
  }
};

const onMouseUp = () => {
  document.removeEventListener("mousemove", onMouseMove);
  document.removeEventListener("mouseup", onMouseUp);
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
  if (dragStartIndex.value !== null && draggedIndex.value !== null && dragStartIndex.value !== draggedIndex.value) {
    dirty.value = true;
  }
  resetDragState();
};

const resetDragState = () => {
  draggedIndex.value = null;
  dragStartIndex.value = null;
  draggedCategory.value = null;
  isDragging.value = false;
};

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", onMouseMove);
  document.removeEventListener("mouseup", onMouseUp);
});
</script>

<style scoped>
.vendor_icon {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  object-fit: cover;
}

.dragging-card {
  background-color: var(--white-opacity-8);
}

.footer {
  border-top: 1px solid var(--white-opacity-8);
}
</style>
